import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { api } from "../api/client";
import { createWS } from "../api/ws";
import { useAuth } from "./AuthContext";
import { ru } from "../locales/ru";

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [online, setOnline] = useState({});
  const wsRef = useRef(null);
  const listenersRef = useRef(new Set());
  const activeChatRef = useRef(null);
  const userRef = useRef(null);
  userRef.current = user;

  const loadChats = useCallback(async () => {
    try {
      const { data } = await api.get("/chats");
      setChats(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const ensureChat = useCallback((chatId, attempt = 0) => {
    loadChats().then((data) => {
      if ((!data || !data.find((c) => c.id === chatId)) && attempt < 3) {
        setTimeout(() => ensureChat(chatId, attempt + 1), 600);
      }
    });
  }, [loadChats]);

  useEffect(() => {
    if (!user) {
      wsRef.current?.close();
      wsRef.current = null;
      setChats([]);
      setOnline({});
      return;
    }
    loadChats();
    const ws = createWS({
      onEvent: (ev) => {
        if (ev.event === "new_message") {
          const { message, chat } = ev.data;
          const me = userRef.current;
          setChats((prev) => {
            const idx = prev.findIndex((c) => c.id === message.chat_id);
            if (idx === -1) {
              ensureChat(message.chat_id);
              return prev;
            }
            const item = { ...prev[idx] };
            item.last_message = {
              text: message.deleted ? ru.chats.deleted : message.type === "image" ? ru.chats.photo + (message.text ? ` · ${message.text}` : "") : message.text,
              id: message.id,
              sender_id: message.sender_id,
              sender_name: message.sender_name,
              created_at: message.created_at,
            };
            item.last_message_at = message.created_at;
            if (message.sender_id !== me?.id && activeChatRef.current !== message.chat_id) {
              item.unread = (item.unread || 0) + 1;
            }
            if (chat?.peer) item.peer = { ...item.peer, ...chat.peer };
            const rest = prev.filter((c) => c.id !== message.chat_id);
            const globalIdx = rest.findIndex((c) => c.type === "global");
            if (item.type === "global") return [item, ...rest];
            return globalIdx === 0 ? [rest[0], item, ...rest.slice(1)] : [item, ...rest];
          });
        } else if (["message_edited", "message_deleted"].includes(ev.event)) {
          loadChats();
        } else if (ev.event === "read_receipt" && ev.data.user_id !== userRef.current?.id) {
          setChats(prev => prev.map(c => c.id === ev.data.chat_id ? { ...c, counterpart_last_read_message_id: c.counterpart_last_read_message_id > ev.data.message_id ? c.counterpart_last_read_message_id : ev.data.message_id } : c));
        } else if (ev.event === "presence") {
          setOnline((o) => ({ ...o, [ev.data.user_id]: ev.data.online }));
        }
        listenersRef.current.forEach((fn) => fn(ev));
      },
      onOpen: () => {
        loadChats();
        listenersRef.current.forEach((fn) => fn({ event: "__reconnected" }));
      },
    });
    wsRef.current = ws;
    return () => {
      ws.close();
      wsRef.current = null;
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = useCallback((event, data) => wsRef.current?.send(event, data) || false, []);

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  const setActiveChat = useCallback((chatId) => {
    activeChatRef.current = chatId;
  }, []);

  const markReadLocal = useCallback((chatId) => {
    setChats((prev) => prev.map((c) => (c.id === chatId ? { ...c, unread: 0 } : c)));
  }, []);

  const totalUnread = chats.reduce((s, c) => s + (c.unread || 0), 0);

  return (
    <ChatContext.Provider
      value={{ chats, loadChats, online, send, subscribe, setActiveChat, markReadLocal, totalUnread }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  return useContext(ChatContext);
}
